import React from "react";
import { useTranslation } from "react-i18next";

import { Can } from "@/casl.config";
import { formatPriceWithCurrency } from "@helpers/utils";
import { useAppSelector } from "@redux/hooks";
import { ContributionMarginSummary, formatMarginPercentage } from "../contributionMargin";

/**
 * Internal contribution margin (dekningsbidrag) rows, rendered right below the order totals.
 *
 * Used both by the order editor and by the saved order view. Both compute the summary from their
 * lines with summarizeContributionMargin, which is what keeps the two identical.
 *
 * Gating lives inside the component on purpose: the editor has no other permission checks, so a
 * gate left to the call sites would be one omission away from exposing cost prices.
 */
export default function ContributionMarginDetails({
    summary,
}: {
    summary: ContributionMarginSummary;
}) {
    const { t } = useTranslation("", { keyPrefix: "sales.orders.general.orders.summary" });

    const baseCurrency = useAppSelector((state) => state.auth.user?.company?.currency);

    return (
        <Can I="price.view" a="sale_order">
            <div className="flex justify-end mt-[16px] font-thin">
                <div className="w-1/2 grid grid-cols-2 gap-y-[16px]">
                    {/* Divider between the totals and the margin rows. The rule is set inline
                        because Tailwind's border utilities currently paint all four sides here. */}
                    <div
                        className="col-span-2"
                        style={{
                            borderTop: "1px solid #D9D6DA",
                            borderRight: "none",
                            borderBottom: "none",
                            borderLeft: "none",
                        }}
                    />

                    <div className="text-right">{t("purchaseCost")}</div>
                    <div className="text-right pr-[16px]">
                        {formatPriceWithCurrency(summary.purchaseCostTotal, baseCurrency)}
                    </div>

                    <div className="text-right">{t("marginPercentage")}</div>
                    <div className="text-right pr-[16px]">
                        {formatMarginPercentage(summary.contributionMarginPercentage)}
                    </div>

                    <div className="text-right font-semibold text-accent">
                        {t("contributionMargin")}
                    </div>
                    <div className="text-right pr-[16px] font-semibold text-accent">
                        {formatPriceWithCurrency(summary.contributionMargin, baseCurrency)}
                    </div>
                </div>
            </div>
        </Can>
    );
}
